import {
  Card,
  Group,
  Avatar,
  Text,
  Badge,
  Progress,
  Stack,
  ActionIcon,
  Menu,
  Divider,
} from "@mantine/core";
import {
  IconUser,
  IconBook,
  IconCards,
  IconTrendingUp,
  IconDotsVertical,
  IconEye,
  IconEdit,
  IconCalendar,
  IconStar,
  IconClock,
  IconPlayerPlay,
  IconMicrophone,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useParentQuickRead } from "@/features/recommendations";
import { capitalizeName } from "@/utils/name";

interface ChildCardProps {
  child: {
    id: string;
    name: string;
    avatar?: string;
    grade?: number;
    readingStreak?: number;
    lastActive?: string;
    fluency_enabled?: boolean;
    stats?: {
      storiesRead: number;
      storiesTotal?: number;
      cardsMastered: number;
      cardsTotal?: number;
      avgQuizScore?: number;
      weeklyGoal?: number;
      weeklyProgress?: number;
    };
    nextAssignment?: {
      title: string;
      dueDate: string;
      type: "story" | "flashcards" | "quiz";
    };
  };
  onQuickAssign?: () => void;
  onViewDetails?: () => void;
}

export function MobileChildCard({ child }: ChildCardProps) {
  const navigate = useNavigate();
  const childDisplayName = capitalizeName(child.name);
  const { startQuickRead, isDisabled: isQuickReadDisabled } = useParentQuickRead({
    studentId: child.id,
    childName: childDisplayName,
  });

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return "green";
    if (progress >= 50) return "blue";
    return "yellow";
  };

  const getStreakBadgeColor = (streak: number) => {
    if (streak >= 30) return "violet";
    if (streak >= 7) return "orange";
    if (streak >= 3) return "yellow";
    return "gray";
  };

  return (
    <Card
      p="md"
      withBorder
      style={{ cursor: "pointer" }}
      onClick={() => navigate(`/mobile/parent/child/${child.id}`)}
    >
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <Avatar size={48} radius="xl" color="blue">
              {child.avatar ? (
                <img src={child.avatar} alt={childDisplayName} />
              ) : (
                <IconUser size={24} />
              )}
            </Avatar>
            <div>
              <Group gap="xs">
                <Text size="md" fw={600}>
                  {childDisplayName}
                </Text>
                {child.grade && (
                  <Badge size="sm" variant="light">
                    Grade {child.grade}
                  </Badge>
                )}
              </Group>
              {child.lastActive && (
                <Group gap={4}>
                  <IconClock size={12} color="var(--mantine-color-dimmed)" />
                  <Text size="xs" c="dimmed">
                    Active {child.lastActive}
                  </Text>
                </Group>
              )}
            </div>
          </Group>
          <Menu position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconPlayerPlay size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  startQuickRead();
                }}
                disabled={isQuickReadDisabled}
                color="teal"
              >
                Read with {childDisplayName}
              </Menu.Item>
              {child.fluency_enabled && (
                <Menu.Item
                  leftSection={<IconMicrophone size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/parent/fluency/start?studentId=${child.id}`);
                  }}
                  color="grape"
                >
                  Read Aloud with {childDisplayName}
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconEye size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/mobile/parent/child/${child.id}`);
                }}
              >
                View Details
              </Menu.Item>
              <Menu.Item
                leftSection={<IconBook size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/mobile/parent/assign/story?childId=${child.id}`);
                }}
              >
                Assign Story
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCards size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/mobile/parent/assign/flashcards?childId=${child.id}`);
                }}
              >
                Assign Flashcards
              </Menu.Item>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/mobile/parent/child/${child.id}/edit`);
                }}
              >
                Edit Profile
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        {/* Reading Streak */}
        {child.readingStreak !== undefined && child.readingStreak > 0 && (
          <Group justify="space-between">
            <Group gap="xs">
              <IconStar size={16} color="var(--mantine-color-orange-6)" />
              <Text size="sm" fw={500}>
                Reading Streak
              </Text>
            </Group>
            <Badge size="lg" variant="light" color={getStreakBadgeColor(child.readingStreak)}>
              {child.readingStreak} days
            </Badge>
          </Group>
        )}

        {/* Stats */}
        {child.stats && (
          <>
            <Divider />
            <div>
              <Text size="xs" fw={600} c="dimmed" mb="xs">
                Progress Overview
              </Text>
              <Stack gap="xs">
                {/* Stories Progress */}
                {child.stats.storiesTotal && (
                  <div>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs">
                        <IconBook size={14} />
                        <Text size="xs">Stories Read</Text>
                      </Group>
                      <Text size="xs" fw={500}>
                        {child.stats.storiesRead}/{child.stats.storiesTotal}
                      </Text>
                    </Group>
                    <Progress
                      value={(child.stats.storiesRead / child.stats.storiesTotal) * 100}
                      color={getProgressColor(
                        (child.stats.storiesRead / child.stats.storiesTotal) * 100
                      )}
                      size="sm"
                    />
                  </div>
                )}

                {/* Cards Progress */}
                {child.stats.cardsTotal && (
                  <div>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs">
                        <IconCards size={14} />
                        <Text size="xs">Cards Mastered</Text>
                      </Group>
                      <Text size="xs" fw={500}>
                        {child.stats.cardsMastered}/{child.stats.cardsTotal}
                      </Text>
                    </Group>
                    <Progress
                      value={(child.stats.cardsMastered / child.stats.cardsTotal) * 100}
                      color={getProgressColor(
                        (child.stats.cardsMastered / child.stats.cardsTotal) * 100
                      )}
                      size="sm"
                    />
                  </div>
                )}

                {/* Weekly Goal */}
                {child.stats.weeklyGoal && (
                  <div>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs">
                        <IconTrendingUp size={14} />
                        <Text size="xs">Weekly Goal</Text>
                      </Group>
                      <Text size="xs" fw={500}>
                        {child.stats.weeklyProgress || 0}/{child.stats.weeklyGoal} sessions
                      </Text>
                    </Group>
                    <Progress
                      value={((child.stats.weeklyProgress || 0) / child.stats.weeklyGoal) * 100}
                      color={getProgressColor(
                        ((child.stats.weeklyProgress || 0) / child.stats.weeklyGoal) * 100
                      )}
                      size="sm"
                    />
                  </div>
                )}

                {/* Quiz Average */}
                {child.stats.avgQuizScore !== undefined && (
                  <Group justify="space-between">
                    <Text size="xs">Average Quiz Score</Text>
                    <Badge
                      size="sm"
                      variant="light"
                      color={getProgressColor(child.stats.avgQuizScore)}
                    >
                      {child.stats.avgQuizScore}%
                    </Badge>
                  </Group>
                )}
              </Stack>
            </div>
          </>
        )}

        {/* Next Assignment */}
        {child.nextAssignment && (
          <>
            <Divider />
            <div>
              <Text size="xs" fw={600} c="dimmed" mb="xs">
                Next Assignment
              </Text>
              <Card p="xs" withBorder style={{ borderStyle: "dashed" }}>
                <Group justify="space-between">
                  <Group gap="xs">
                    {child.nextAssignment.type === "story" && <IconBook size={16} />}
                    {child.nextAssignment.type === "flashcards" && <IconCards size={16} />}
                    <div>
                      <Text size="sm" fw={500} lineClamp={1}>
                        {child.nextAssignment.title}
                      </Text>
                      <Group gap={4}>
                        <IconCalendar size={12} color="var(--mantine-color-dimmed)" />
                        <Text size="xs" c="dimmed">
                          Due {new Date(child.nextAssignment.dueDate).toLocaleDateString()}
                        </Text>
                      </Group>
                    </div>
                  </Group>
                </Group>
              </Card>
            </div>
          </>
        )}
      </Stack>
    </Card>
  );
}
